import React, { useState, useMemo } from 'react';
import { 
  ArrowLeft, 
  ChefHat, 
  Plus, 
  Trash2, 
  Clock, 
  Package, 
  DollarSign, 
  Search,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  TrendingUp,
  Zap,
  Send,
  Loader2,
  Camera,
  Image as ImageIcon,
  Paperclip,
  FileText,
  FileSpreadsheet,
  X,
  Download,
  FileDown,
  PlayCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { TUTORIAL_LINKS } from '../constants';
import { UserProfile, FinancialConfig, Material, ProductTemplate, TemplateComponent } from '../types';
import { formatCurrencyBRL, formatMinutes, parseFlexibleTimeToMinutes } from '../utils/formatUtils';
import { useRecipeAttachments } from '../hooks/useRecipeAttachments';
import { useRecipeAI, PendingRecipe, PendingRecipeItem, findBestMaterialMatch } from '../hooks/useRecipeAI';
import { useRecipeEditor } from '../hooks/useRecipeEditor';
import { useNicheRecipeTemplates } from '../hooks/useNicheRecipeTemplates';
import { ImageCaptureMenu } from './CostCenter';
import { consumeRecipeDraft, BRIDGE_EVENTS } from '../modules/erp/utils/commandBridge';

interface Props {
  user: UserProfile;
  config: FinancialConfig;
  materials: Material[];
  recipes: ProductTemplate[];
  onSaveRecipe: (recipe: ProductTemplate) => void;
  onSaveMaterials: (materials: Material[]) => void;
  onDeleteRecipe: (id: string) => void;
  onBack: () => void;
}

export default function RecipeManager({ user, config, materials, recipes, onSaveRecipe, onSaveMaterials, onDeleteRecipe, onBack }: Props) {
  const {
    searchTerm,
    setSearchTerm,
    isModalOpen,
    setIsModalOpen,
    editingRecipe,
    setEditingRecipe,
    filteredRecipes,
    calculateRecipeCosts,
    handleCreateNew,
    handleEdit,
    handleAddItem,
    updateItem,
    removeItem,
    handleSave
  } = useRecipeEditor({
    userId: user.id,
    materials,
    config,
    recipes
  });

  const {
    isUploading,
    handleFileUpload,
    removeAttachment,
    getFileIcon
  } = useRecipeAttachments({
    userId: user.id,
    editingRecipe,
    setEditingRecipe
  });

  const {
    inputValue,
    setInputValue,
    isProcessing,
    processingStep,
    pendingRecipes,
    setPendingRecipes,
    processWithAI,
    handleImageExtraction,
    confirmPendingRecipe,
    updatePendingRecipeField,
    updatePendingItem
  } = useRecipeAI({
    catalog: materials,
    onSaveRecipe,
    onSaveMaterials,
    userId: user.id
  });

  // Ponte Omnibar -> Fichas Técnicas / Receitas: consome o rascunho e insere na fila de validação
  React.useEffect(() => {
    const checkDraft = () => {
      const draft = consumeRecipeDraft(user.id);
      if (draft && draft.name) {
        const newPending: PendingRecipe = {
          name: draft.name,
          executionTimeMinutes: draft.preparationTimeMinutes || 0,
          description: [
            draft.yieldAmount ? `Rendimento: ${draft.yieldAmount} ${draft.yieldUnit || 'un'}` : '',
            draft.instructions || ''
          ].filter(Boolean).join(' | '),
          category: 'recipe',
          items: (draft.items || []).map((it, idx) => {
            const matchedMatId = findBestMaterialMatch(it.name, materials);
            const matchedMat = materials.find(m => m.id === matchedMatId);
            return {
              id: `item_draft_${idx}_${Date.now()}`,
              templateId: '',
              materialId: matchedMatId || '',
              quantity: it.quantity || 1,
              unit: it.unit || 'un',
              cost: it.cost || (matchedMat ? Number(matchedMat.pricePaid || 0) : 0),
              _tempName: it.name,
              _tempUnit: it.unit || 'un',
              _tempPrice: it.cost || 0,
              _isNew: !matchedMatId
            } as PendingRecipeItem;
          })
        };
        setPendingRecipes(prev => [newPending, ...prev]);
      }
    };

    checkDraft();
    window.addEventListener(BRIDGE_EVENTS.recipes, checkDraft);
    return () => window.removeEventListener(BRIDGE_EVENTS.recipes, checkDraft);
  }, [user.id, materials, setPendingRecipes]);

  const [isCaptureMenuOpen, setIsCaptureMenuOpen] = useState(false);
  const cameraInputRef = React.useRef<HTMLInputElement>(null);
  const galleryInputRef = React.useRef<HTMLInputElement>(null);

  const isShrunk = false;

  const { suggestions: recipeSuggestions, dismissSuggestion: dismissRecipeSuggestion } = useNicheRecipeTemplates({
    userNiche: user.niche || '',
    existingRecipes: recipes,
    userId: user.id,
  });

  const [recipeFilter, setRecipeFilter] = useState<'all' | 'recipes' | 'kits'>('all');

  const isKitItem = (r: ProductTemplate) => {
    return r.category === 'kit' || r.category === 'combo' || /kit|combo|sortido|cento|misto/i.test(r.name);
  };

  const displayRecipes = useMemo(() => {
    return filteredRecipes.filter(r => {
      const isKit = isKitItem(r);
      if (recipeFilter === 'recipes') return !isKit;
      if (recipeFilter === 'kits') return isKit;
      return true;
    });
  }, [filteredRecipes, recipeFilter]);

  const countKits = useMemo(() => recipes.filter(isKitItem).length, [recipes]);
  const countRecipesOnly = useMemo(() => recipes.length - countKits, [recipes, countKits]);

  const handleQuickAddRecipe = (s: { id: string; name: string; executionTimeMinutes: number; description?: string }) => {
    setEditingRecipe({
      id: crypto.randomUUID(),
      userId: user.id,
      name: s.name,
      executionTimeMinutes: s.executionTimeMinutes || 60,
      items: [],
    });
    setIsModalOpen(true);
  };

  return (
    <div className="pb-32 font-sans relative">
      <div className="px-4 pt-6 space-y-8">
        {/* AI Extraction Card */}
        <div className="relative z-10">
          <div className={`bg-white border border-zinc-100 shadow-xl border-b-4 border-b-zinc-200 transition-all duration-300 ${
            isShrunk ? 'rounded-none p-3' : 'rounded-4xl p-6'
          }`}>
            <AnimatePresence mode="wait">
              {!isShrunk ? (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-4"
                >
                  <div className="flex items-center gap-2">
                    <div className="bg-amber-500/10 p-2 rounded-2xl">
                      <Zap className="h-5 w-5 text-amber-600" />
                    </div>
                    <h3 className="font-black text-zinc-900 uppercase text-xs tracking-widest">Extração de Receitas & Kits / Combos</h3>
                  </div>
                  
                  <div className="relative">
                    <textarea
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Ex: Combo Salgado + Refri: leva 1 coxinha + 1 refrigerante lata | Cento de Salgados Misto: 25 coxinhas, 25 kibes, 25 bolinhas de queijo, 25 risoles | Bolo de Pote: 30min, 1 leite condensado, 4 ovos..."
                      className="w-full bg-zinc-50 border-none rounded-2xl p-5 text-sm focus:ring-2 focus:ring-amber-500/20 min-h-30 resize-none pb-12"
                    />
                    <div className="absolute bottom-3 left-3 flex gap-2">
                      <input type="file" ref={cameraInputRef} capture="environment" accept="image/*" className="hidden" onChange={handleImageExtraction} />
                      <input type="file" ref={galleryInputRef} accept="image/*" className="hidden" onChange={handleImageExtraction} />
                      <button
                        type="button"
                        onClick={() => setIsCaptureMenuOpen(true)}
                        className="bg-white border border-zinc-200 p-2 rounded-xl text-zinc-500 hover:text-amber-600 hover:border-amber-500 transition-all shadow-sm flex items-center gap-2 active:scale-95 text-[10px] font-bold uppercase tracking-widest cursor-pointer"
                      >
                        <Camera className="h-4 w-4" />
                        <span>Anexar Imagem</span>
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => inputValue && processWithAI(inputValue)}
                    disabled={isProcessing || !inputValue}
                    className="w-full bg-amber-600 text-white py-4 rounded-3xl font-black text-xs uppercase tracking-widest hover:bg-amber-700 disabled:opacity-50 transition-all flex items-center justify-center gap-3 active:scale-95 cursor-pointer"
                  >
                    {isProcessing ? (
                      <div className="flex items-center gap-3">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>{processingStep}</span>
                      </div>
                    ) : (
                      <>
                        <Send className="h-5 w-5 text-amber-200" />
                        <span>Extrair Receita ou Kit / Combo</span>
                      </>
                    )}
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    <Zap className="h-5 w-5 text-amber-500 shrink-0" />
                    <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest truncate">
                      {isProcessing ? processingStep : 'Toque para extrair nova receita'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <label className="cursor-pointer bg-amber-500 text-white p-2.5 rounded-xl shadow-lg shadow-amber-500/20 active:scale-95 transition-all">
                      <Camera className="h-5 w-5" />
                      <input type="file" capture="environment" accept="image/*" className="hidden" onChange={handleImageExtraction} />
                    </label>
                    <button 
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="bg-zinc-900 text-white p-2.5 rounded-xl active:scale-95 transition-all"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Validation Queue (Pending Recipes) */}
        {pendingRecipes.length > 0 && (
          <div className="bg-white border-2 border-amber-500/20 rounded-3xl p-5 shadow-xl animate-in fade-in zoom-in-95 duration-300">
            <div className="flex items-center justify-between mb-5 border-b border-zinc-50 pb-4">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-amber-500" /> 
                Revisando o trabalho do Chef R-Max
              </h4>
              <span className="bg-amber-500/10 text-amber-600 font-black text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full ring-1 ring-amber-500/20">
                Faltam {pendingRecipes.length}
              </span>
            </div>
            
            {(() => {
              const recipe = pendingRecipes[0];
              return (
                <div key={recipe.id} className="animate-in slide-in-from-right-4 duration-300">
                  <div className="flex flex-col gap-5 mb-6">
                    <p className="text-[11px] text-zinc-500 italic px-2">
                      Dê uma olhada rápida se os ingredientes e quantidades estão corretos.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                      <div className="sm:col-span-3">
                        <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2 block px-2">Nome da Receita/Item</label>
                        <input
                          type="text"
                          value={recipe.name || ''}
                          onChange={(e) => updatePendingRecipeField(recipe.id!, 'name', e.target.value)}
                          className="w-full bg-zinc-50/50 border border-zinc-200 rounded-2xl py-4 px-5 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all placeholder:text-zinc-300"
                        />
                      </div>
                      <div className="sm:col-span-1">
                        <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2 block px-2">Tempo ({formatMinutes(recipe.executionTimeMinutes || 0)})</label>
                        <div className="relative">
                          <Clock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                          <input
                            type="text"
                            defaultValue={recipe.executionTimeMinutes ? `${recipe.executionTimeMinutes} min` : ''}
                            placeholder="Ex: 1h 30m ou 90"
                            onBlur={(e) => updatePendingRecipeField(recipe.id!, 'executionTimeMinutes', parseFlexibleTimeToMinutes(e.target.value))}
                            onChange={(e) => updatePendingRecipeField(recipe.id!, 'executionTimeMinutes', parseFlexibleTimeToMinutes(e.target.value))}
                            className="w-full bg-zinc-50/50 border border-zinc-200 rounded-2xl py-4 pl-11 pr-4 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-zinc-50/50 rounded-3xl p-4 border border-zinc-100">
                      <div className="flex items-center gap-2 mb-4 px-1">
                        <Package className="h-4 w-4 text-amber-500" />
                        <h5 className="text-[10px] font-black uppercase text-zinc-400 tracking-widest">Lista de Ingredientes e Materiais</h5>
                      </div>
                      
                      <div className="space-y-3">
                        {recipe.items?.map((item, idx) => {
                          const i = item as PendingRecipeItem;
                          const matchStatus = i.materialId ? 'success' : 'warning';
                          const isNew = i._isNew || !i.materialId;

                          return (
                            <div key={i.id} className={`grid grid-cols-1 gap-4 p-4 rounded-3xl border ${matchStatus === 'warning' ? 'border-amber-200 bg-amber-50/30' : 'border-zinc-200 bg-white shadow-sm'}`}>
                              <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                  <label className="text-[9px] font-black text-zinc-400 uppercase mb-1 flex items-center justify-between px-1">
                                    <span>{isNew ? 'Nome do Novo Material' : `Material Identificado: ${i._tempName}`}</span>
                                    {isNew && <span className="text-amber-500 flex items-center gap-1 font-bold whitespace-nowrap"><AlertCircle className="h-3 w-3"/> Novo Material</span>}
                                  </label>
                                  {isNew ? (
                                    <input
                                      type="text"
                                      value={i._tempName || ''}
                                      onChange={(e) => updatePendingItem(recipe.id!, i.id, '_tempName', e.target.value)}
                                      className="w-full bg-white border border-amber-400 ring-2 ring-amber-400/20 shadow-amber-500/10 rounded-2xl py-3.5 px-4 text-xs font-bold focus:outline-none focus:border-amber-500 transition-all mb-2"
                                      placeholder="Nome do Material"
                                    />
                                  ) : null}
                                  <div className="relative">
                                    <select
                                      value={i.materialId}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        updatePendingItem(recipe.id!, i.id, 'materialId', val);
                                        updatePendingItem(recipe.id!, i.id, '_isNew', !val);
                                        if (val) {
                                          const mat = materials.find(m => m.id === val);
                                          if (mat) updatePendingItem(recipe.id!, i.id, '_tempName', mat.name);
                                        }
                                      }}
                                      className={`w-full appearance-none bg-white border ${!i.materialId ? 'border-amber-400 ring-2 ring-amber-400/20 shadow-amber-500/10' : 'border-emerald-200'} rounded-2xl py-3.5 pl-4 pr-10 text-xs font-bold focus:outline-none focus:border-amber-500 transition-all`}
                                    >
                                      <option value="">{isNew ? '--- Vincular a um existente? ---' : '(Cadastrar como Novo Material)'}</option>
                                      {materials.map(m => (
                                        <option key={m.id} value={m.id}>{m.name} ({m.unit}) - {formatCurrencyBRL(m.pricePaid)}</option>
                                      ))}
                                    </select>
                                    <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 rotate-90 pointer-events-none" />
                                  </div>
                                </div>
                                <div className="w-full sm:w-32">
                                  <label className="text-[9px] font-black text-zinc-400 uppercase mb-1 block px-1">Qtd Usada</label>
                                  <div className="relative">
                                    <input
                                      type="number"
                                      value={item.quantity}
                                      onChange={(e) => updatePendingItem(recipe.id!, item.id, 'quantity', Number(e.target.value))}
                                      className="w-full bg-white border border-zinc-200 rounded-2xl py-3.5 px-4 text-xs font-bold focus:outline-none focus:border-amber-500 transition-all"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-zinc-400 font-bold uppercase">
                                      {i._tempUnit || 'un'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {isNew && (
                                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-amber-200/30 animate-in fade-in slide-in-from-top-2">
                                  <div>
                                    <label className="text-[8px] font-black text-amber-600/60 uppercase mb-1 block px-1">Unidade Base</label>
                                    <input
                                      type="text"
                                      value={i._tempUnit || ''}
                                      onChange={(e) => updatePendingItem(recipe.id!, i.id, '_tempUnit', e.target.value)}
                                      placeholder="Ex: kg, un"
                                      className="w-full bg-amber-50/50 border border-amber-100 rounded-xl py-2 px-3 text-[10px] font-bold focus:outline-none focus:border-amber-400 transition-all"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-[8px] font-black text-amber-600/60 uppercase mb-1 block px-1">Preço Pago (R$)</label>
                                    <input
                                      type="number"
                                      value={i._tempPrice || 0}
                                      onChange={(e) => updatePendingItem(recipe.id!, i.id, '_tempPrice', Number(e.target.value))}
                                      className="w-full bg-amber-50/50 border border-amber-100 rounded-xl py-2 px-3 text-[10px] font-bold focus:outline-none focus:border-amber-400 transition-all"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                  </div>
                  
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => {
                        const isValid = recipe.items?.every(item => Boolean((item as PendingRecipeItem).materialId) || (item as PendingRecipeItem)._isNew);
                        if (!isValid) {
                          alert("Por favor, selecione ou cadastre o material na lista antes de salvar.");
                          return;
                        }
                        confirmPendingRecipe(recipe);
                      }}
                      className={`w-full ${
                        recipe.items?.every(item => Boolean((item as PendingRecipeItem).materialId) || (item as PendingRecipeItem)._isNew) 
                          ? 'bg-amber-600 shadow-amber-600/20 text-white hover:bg-amber-700' 
                          : 'bg-amber-200 text-amber-900 border border-amber-300'
                      } py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg active:scale-95 transition-all cursor-pointer`}
                    >
                      {recipe.items?.every(item => Boolean((item as PendingRecipeItem).materialId) || (item as PendingRecipeItem)._isNew) 
                        ? (pendingRecipes.length > 1 ? 'Tudo Certo! Confirmar e Próximo' : 'Tudo Certo! Salvar Receita') 
                        : '⚠️ Faltam Materiais (Toque para verificar)'}
                    </button>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setPendingRecipes(prev => prev.slice(1))}
                        className="flex-1 bg-zinc-100 text-zinc-400 py-3 rounded-2xl hover:text-amber-600 hover:bg-amber-50 transition-colors font-bold uppercase text-[10px] tracking-widest"
                      >
                        Pular esta
                      </button>
                      <button
                        onClick={() => {
                          if (confirm("Deseja descartar esta extração e tentar novamente?")) {
                            setPendingRecipes(prev => prev.filter(p => p.id !== recipe.id));
                          }
                        }}
                        className="flex-1 bg-rose-50 text-rose-500 py-3 rounded-2xl hover:bg-rose-100 transition-colors font-bold uppercase text-[10px] tracking-widest"
                      >
                        Descartar e Refazer
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Recipe List Section */}
        <div className="space-y-6">
          <div className="flex items-center justify-between px-1 flex-wrap gap-2">
            <div>
              <h3 className="text-xs font-black text-zinc-900 uppercase tracking-widest">Catálogo de Receitas & Kits ({recipes.length})</h3>
              <p className="text-[11px] text-zinc-500 font-medium">Cadastre receitas de produção ou monte combos (ex: Salgado + Refri, Centos Sortidos).</p>
            </div>
            <div className="flex items-center gap-2">
              <a 
                href={TUTORIAL_LINKS.receitas}
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-600 bg-amber-50 border border-amber-200/60 px-2.5 py-1 rounded-xl text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 hover:bg-amber-100 transition-colors"
              >
                <PlayCircle className="h-3.5 w-3.5 text-amber-600" />
                Vídeo Tutorial
              </a>
              <button 
                onClick={handleCreateNew}
                className="bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer active:scale-95"
              >
                <Plus className="h-3.5 w-3.5" />
                Cadastrar Novo
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <input 
                type="text"
                placeholder="Buscar por receita, kit ou combo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white border border-zinc-200 rounded-2xl py-3.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 shadow-sm transition-all"
              />
            </div>

            {/* Filtros: Todos / Receitas / Kits */}
            <div className="flex bg-zinc-100 p-1 rounded-2xl text-xs font-bold shrink-0">
              <button
                type="button"
                onClick={() => setRecipeFilter('all')}
                className={`px-3 py-1.5 rounded-xl transition-all cursor-pointer ${
                  recipeFilter === 'all' ? 'bg-white text-zinc-900 shadow-xs font-black' : 'text-zinc-500 hover:text-zinc-900'
                }`}
              >
                Todos ({recipes.length})
              </button>
              <button
                type="button"
                onClick={() => setRecipeFilter('recipes')}
                className={`px-3 py-1.5 rounded-xl transition-all cursor-pointer ${
                  recipeFilter === 'recipes' ? 'bg-white text-zinc-900 shadow-xs font-black' : 'text-zinc-500 hover:text-zinc-900'
                }`}
              >
                🍰 Receitas ({countRecipesOnly})
              </button>
              <button
                type="button"
                onClick={() => setRecipeFilter('kits')}
                className={`px-3 py-1.5 rounded-xl transition-all cursor-pointer ${
                  recipeFilter === 'kits' ? 'bg-white text-zinc-900 shadow-xs font-black' : 'text-zinc-500 hover:text-zinc-900'
                }`}
              >
                📦 Kits & Combos ({countKits})
              </button>
            </div>
          </div>

          {/* Suggestions Panel */}
          {recipeSuggestions.length > 0 && (
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 px-1">
                <Zap className="h-3 w-3 text-amber-500" />
                <h3 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Sugeridos para {user.niche}</h3>
              </div>
              <div className="flex flex-col gap-2">
                {recipeSuggestions.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between bg-white border border-zinc-100 rounded-2xl px-4 py-3 shadow-sm"
                  >
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold text-zinc-700 truncate">{s.name}</p>
                      <p className="text-[9px] text-zinc-400 uppercase tracking-wide mt-0.5">
                        {s.executionTimeMinutes} min de preparo
                        {s.description && ` · ${s.description}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 shrink-0">
                      <button
                        onClick={() => handleQuickAddRecipe(s)}
                        className="bg-amber-100 hover:bg-amber-200 text-amber-700 text-[10px] font-black px-3 py-1.5 rounded-xl transition-colors flex items-center gap-1 cursor-pointer"
                      >
                        <Plus className="h-3 w-3" />
                        Adicionar
                      </button>
                      <button
                        onClick={() => dismissRecipeSuggestion(s.id)}
                        title="Não preciso desta sugestão"
                        className="text-zinc-300 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-50 cursor-pointer"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recipe & Kit List */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {displayRecipes.length === 0 ? (
              <div className="col-span-full py-16 text-center bg-white rounded-4xl border-2 border-dashed border-zinc-100">
                <ChefHat className="h-16 w-16 text-zinc-200 mx-auto mb-4" />
                <h4 className="text-zinc-900 font-bold mb-1">Nenhum item encontrado</h4>
                <p className="text-zinc-400 text-xs max-w-60 mx-auto leading-relaxed">
                  {searchTerm ? 'Tente buscar por outro termo ou cadastre um novo item acima.' : 'Cadastre sua primeira receita ou kit/combo agora mesmo!'}
                </p>
              </div>
            ) : (
              displayRecipes.map(recipe => {
                const { materialCost, totalCost } = calculateRecipeCosts(recipe);
                const isKit = isKitItem(recipe);

                return (
                  <button
                    key={recipe.id}
                    onClick={() => handleEdit(recipe)}
                    className="bg-white p-5 rounded-3xl border border-zinc-100 shadow-sm hover:border-amber-500 transition-all text-left flex flex-col group relative overflow-hidden cursor-pointer"
                  >
                    <div className="absolute top-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ChevronRight className="h-5 w-5 text-amber-500" />
                    </div>

                    <div className="flex justify-between items-start mb-3">
                      <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-lg border ${
                        isKit 
                          ? 'bg-purple-50 text-purple-900 border-purple-200' 
                          : 'bg-rose-50 text-rose-900 border-rose-200'
                      }`}>
                        {isKit ? '📦 Kit / Combo' : '🍰 Receita'}
                      </span>
                      {recipe.items?.some(i => !materials.find(m => m.id === i.materialId)) && (
                        <div className="bg-rose-50 text-rose-500 p-1.5 rounded-xl" title="Existem itens sem material vinculado!">
                          <AlertCircle className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                    
                    <h3 className="text-base font-bold text-zinc-900 mb-1 truncate pr-6">{recipe.name}</h3>
                    <div className="flex items-center gap-4 text-xs text-zinc-400 mb-4">
                      <span className="flex items-center gap-1.5 font-medium">
                        <Clock className="h-3.5 w-3.5 text-zinc-300" />
                        {recipe.executionTimeMinutes} min
                      </span>
                      <span className="flex items-center gap-1.5 font-medium">
                        <Package className="h-3.5 w-3.5 text-zinc-300" />
                        {recipe.items?.length || 0} componentes
                      </span>
                    </div>

                    <div className="mt-auto pt-4 border-t border-zinc-50 flex justify-between items-end">
                      <div>
                        <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-0.5">Custo dos Insumos</p>
                        <p className="text-xs font-bold text-zinc-600">{formatCurrencyBRL(materialCost)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-0.5">Preço Sugerido</p>
                        <p className="text-lg font-black text-zinc-900">{formatCurrencyBRL(totalCost)}</p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

      {/* Recipe / Kit Modal */}
      <AnimatePresence>
        {isModalOpen && editingRecipe && (
          <div className="fixed inset-0 z-100 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-zinc-900/80 backdrop-blur-sm" 
            />
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              className="relative w-full max-w-2xl bg-white rounded-t-4xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              <div className="p-5 sm:p-6 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/50">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-zinc-900">
                    {editingRecipe.id ? 'Editar Receita ou Kit' : 'Nova Receita ou Kit / Combo'}
                  </h2>
                  <p className="text-xs text-zinc-500">Defina os componentes (insumos, produtos ou embalagens) e o tempo de montagem.</p>
                </div>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="bg-zinc-200/50 hover:bg-zinc-200 p-2 rounded-full transition-colors cursor-pointer"
                >
                  <ArrowLeft className="h-5 w-5 text-zinc-600 rotate-180" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
                {/* Seletor de Tipo: Receita vs Kit */}
                <div>
                  <label className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.15em] block mb-1.5">Tipo do Cadastro</label>
                  <div className="grid grid-cols-2 gap-2 bg-zinc-100 p-1 rounded-2xl text-xs font-bold">
                    <button
                      type="button"
                      onClick={() => setEditingRecipe(prev => prev ? { ...prev, category: 'recipe' } : null)}
                      className={`py-2 px-3 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                        (!editingRecipe.category || editingRecipe.category === 'recipe')
                          ? 'bg-amber-500 text-white shadow-xs font-black'
                          : 'text-zinc-600 hover:text-zinc-900'
                      }`}
                    >
                      <span>🍰</span>
                      <span>Receita / Ficha Técnica</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingRecipe(prev => prev ? { ...prev, category: 'kit' } : null)}
                      className={`py-2 px-3 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                        editingRecipe.category === 'kit' || editingRecipe.category === 'combo'
                          ? 'bg-amber-500 text-white shadow-xs font-black'
                          : 'text-zinc-600 hover:text-zinc-900'
                      }`}
                    >
                      <span>📦</span>
                      <span>Kit / Combo de Produtos</span>
                    </button>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1.5 font-medium">
                    {editingRecipe.category === 'kit' || editingRecipe.category === 'combo'
                      ? '📦 Ideal para combos (ex: Salgado + Refrigerante), centos sortidos (25 de cada sabor), kits festa ou pacotes promocionais.'
                      : '🍰 Ideal para calcular o custo exato de produção a partir de ingredientes básicos e tempo de forno/fogão.'}
                  </p>
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.15em] block mb-2">
                      {editingRecipe.category === 'kit' || editingRecipe.category === 'combo' ? 'Nome do Kit / Combo' : 'Nome da Receita'}
                    </label>
                    <input 
                      type="text" 
                      placeholder={editingRecipe.category === 'kit' ? 'Ex: Combo Salgado + Refri Lata' : 'Ex: Bolo de Chocolate 2kg'}
                      value={editingRecipe.name}
                      onChange={e => setEditingRecipe({...editingRecipe, name: e.target.value})}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-3.5 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.15em] block mb-2">
                      Tempo de {editingRecipe.category === 'kit' ? 'Montagem' : 'Execução'} ({formatMinutes(editingRecipe.executionTimeMinutes || 0)})
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                      <input 
                        type="text" 
                        defaultValue={editingRecipe.executionTimeMinutes ? `${editingRecipe.executionTimeMinutes} min` : ''}
                        placeholder="Ex: 15 min, 1h ou 90"
                        onBlur={e => setEditingRecipe({...editingRecipe, executionTimeMinutes: parseFlexibleTimeToMinutes(e.target.value)})}
                        onChange={e => setEditingRecipe({...editingRecipe, executionTimeMinutes: parseFlexibleTimeToMinutes(e.target.value)})}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-3.5 pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 font-bold"
                      />
                    </div>
                  </div>
                </div>

                {/* Items Section */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-xs font-black text-zinc-400 uppercase tracking-[0.15em]">
                      {editingRecipe.category === 'kit' ? 'Componentes do Kit / Produtos' : 'Ingredientes / Materiais'}
                    </h3>
                    <button 
                      onClick={handleAddItem}
                      className="text-xs font-bold text-amber-600 flex items-center gap-1 hover:bg-amber-50 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                    >
                      <Plus className="h-3 w-3" />
                      Adicionar Item
                    </button>
                  </div>

                  <div className="space-y-3">
                    {editingRecipe.items?.length === 0 ? (
                      <div 
                        onClick={handleAddItem}
                        className="py-10 text-center border-2 border-dashed border-zinc-100 rounded-2xl cursor-pointer hover:bg-zinc-50 transition-colors"
                      >
                        <Package className="h-8 w-8 text-zinc-200 mx-auto mb-2" />
                        <p className="text-xs text-zinc-400 font-medium">Toque para adicionar o primeiro item</p>
                      </div>
                    ) : (
                      editingRecipe.items?.map((item) => {
                        const material = materials.find(m => m.id === item.materialId);
                        const unitPrice = material ? (material.pricePaid / material.yieldAmount) * item.quantity : 0;
                        
                        return (
                          <div key={item.id} className="bg-zinc-50/50 border border-zinc-100 p-4 rounded-2xl group relative">
                            <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-center">
                              {/* Material Select */}
                              <div className="sm:col-span-6">
                                <label className="text-[9px] font-bold text-zinc-400 uppercase mb-1 flex items-center justify-between px-1">
                                  <span>Material</span>
                                  {!material && <span className="text-rose-500 flex items-center gap-1 font-bold"><AlertCircle className="h-3 w-3"/> Excluído ou não encontrado</span>}
                                </label>
                                <select 
                                  value={item.materialId}
                                  onChange={e => updateItem(item.id, 'materialId', e.target.value)}
                                  className={`w-full appearance-none bg-white border ${!material ? 'border-rose-400 ring-2 ring-rose-400/20' : 'border-zinc-200'} rounded-xl py-2.5 px-3 text-xs font-bold focus:outline-none`}
                                >
                                  {!material && <option value="">(Material Excluído do Centro de Custos)</option>}
                                  {materials.map(m => (
                                    <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>
                                  ))}
                                </select>
                              </div>

                              {/* Quantity */}
                              <div className="sm:col-span-3">
                                <label className="text-[9px] font-bold text-zinc-400 uppercase mb-1 block">Qtd Usada</label>
                                <div className="relative">
                                  <input 
                                    type="number" 
                                    value={item.quantity}
                                    onChange={e => updateItem(item.id, 'quantity', Number(e.target.value))}
                                    className="w-full bg-white border border-zinc-200 rounded-xl py-2.5 px-3 text-xs font-bold focus:outline-none"
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-400 font-bold">
                                    {material?.unit || 'un'}
                                  </span>
                                </div>
                              </div>

                              {/* Cost Preview */}
                              <div className="sm:col-span-2 text-right">
                                <label className="text-[9px] font-bold text-zinc-400 uppercase mb-1 block text-right">Subtotal</label>
                                <p className="text-xs font-bold text-zinc-900">{formatCurrencyBRL(unitPrice)}</p>
                              </div>

                              {/* Remove */}
                              <div className="sm:col-span-1 flex justify-end">
                                <button 
                                  onClick={() => removeItem(item.id)}
                                  className="p-2 text-zinc-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Summary Section */}
                <div className="bg-zinc-900 rounded-3xl p-6 text-white shadow-xl">
                  <div className="flex items-center gap-2 mb-4 text-amber-500">
                    <TrendingUp className="h-4 w-4" />
                    <h3 className="text-xs font-black uppercase tracking-[0.2em]">Estimativa de Custo</h3>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-zinc-400">Total de Materiais</span>
                      <span className="font-bold">{formatCurrencyBRL(calculateRecipeCosts(editingRecipe).materialCost)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-zinc-400">Tempo de Produção ({editingRecipe.executionTimeMinutes} min)</span>
                      <span className="font-bold text-amber-100">+{formatCurrencyBRL(calculateRecipeCosts(editingRecipe).laborCost)}</span>
                    </div>
                    <div className="h-px bg-zinc-800" />
                    <div className="flex justify-between items-end">
                      <span className="text-zinc-400 text-xs">Preço de Custo Sugerido</span>
                      <span className="text-3xl font-black text-white">{formatCurrencyBRL(calculateRecipeCosts(editingRecipe).totalCost)}</span>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-2xl p-4 flex gap-3 items-start">
                    <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-zinc-400 leading-relaxed italic">
                      Este é o custo base para **este modelo**. Ao gerar um orçamento real, você ainda poderá adicionar sua margem de lucro e impostos para chegar no preço final de venda.
                    </p>
                  </div>
                </div>

                {/* Attachments Section */}
                <div className="border-t border-zinc-100 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                      <Paperclip className="h-4 w-4 text-zinc-400" />
                      <h3 className="text-xs font-black text-zinc-400 uppercase tracking-[0.15em]">Documentação e Anexos</h3>
                    </div>
                    <label className={`cursor-pointer px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-2 ${
                      isUploading ? 'bg-zinc-50 text-zinc-400 cursor-not-allowed' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-600'
                    }`}>
                      {isUploading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Plus className="h-3.5 w-3.5" />
                      )}
                      {isUploading ? 'Enviando...' : 'Anexar Arquivo'}
                      <input type="file" className="hidden" onChange={handleFileUpload} disabled={isProcessing || isUploading} />
                    </label>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {editingRecipe.attachments?.map((file) => (
                      <div key={file.id} className="bg-zinc-50 border border-zinc-100 p-3 rounded-2xl flex items-center justify-between group">
                        <div className="flex items-center gap-3 overflow-hidden">
                          <div className="bg-white p-2 rounded-xl border border-zinc-100">
                            {getFileIcon(file.type)}
                          </div>
                          <div className="overflow-hidden">
                            <p className="text-xs font-bold text-zinc-900 truncate pr-2">{file.name}</p>
                            <p className="text-[9px] text-zinc-400 uppercase">{(file.size / 1024).toFixed(0)} KB</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <a 
                            href={file.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="p-2 text-zinc-400 hover:text-[#004B87] transition-colors"
                          >
                            <FileDown className="h-4 w-4" />
                          </a>
                          <button 
                            onClick={() => removeAttachment(file.id)}
                            className="p-2 text-zinc-400 hover:text-rose-500 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {(!editingRecipe.attachments || editingRecipe.attachments.length === 0) && (
                      <div className="sm:col-span-2 py-8 text-center border-2 border-dashed border-zinc-50 rounded-2xl">
                        <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest">Nenhum arquivo anexado</p>
                        <p className="text-[9px] text-zinc-300 mt-1">Suba fotos, PDFs, Word ou Excel para documentar sua receita.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6 bg-zinc-50 border-t border-zinc-100 flex flex-col sm:flex-row gap-3">
                <button 
                  onClick={() => handleSave(onSaveRecipe)}
                  className="flex-1 bg-amber-600 text-white py-4 rounded-2xl font-black text-sm shadow-xl shadow-amber-600/20 hover:bg-amber-700 transition-all flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="h-5 w-5" />
                  Salvar Produto Modelo
                </button>
                {editingRecipe.id && (
                  <button 
                    onClick={() => {
                      if (confirm('Deseja excluir esta receita permanentemente?')) {
                        onDeleteRecipe(editingRecipe.id!);
                        setIsModalOpen(false);
                      }
                    }}
                    className="sm:w-auto bg-white border border-rose-100 text-rose-500 px-6 py-4 rounded-2xl font-bold text-sm hover:bg-rose-50 transition-all"
                  >
                    Excluir
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <ImageCaptureMenu
        isOpen={isCaptureMenuOpen}
        onClose={() => setIsCaptureMenuOpen(false)}
        onSelectCamera={() => cameraInputRef.current?.click()}
        onSelectGallery={() => galleryInputRef.current?.click()}
        title="Anexar Receita"
        subtitle="Selecione a origem da imagem da receita"
      />
    </div>
    </div>
  );
}
